import {
  GameState, Effect, SHOT_SPEED, LAUNCHER_X, LAUNCHER_Y, R, ROW_H,
  DROP_INTERVAL, BEST_KEY, Color, W, H,
} from './types';
import {
  createState, applyShot, dropCeiling, isWin, isLost, pickColor,
  colorsOnField, occupiedSet, aimToward,
} from './game';
import { snapCell, cellCenter } from './grid';
import { advanceProjectile } from './physics';
import { setupCanvas, loadBackground, readTheme, renderFrame, bubbleCenter } from './renderer';
import { attachInput } from './input';
import { track } from './analytics';
import {
  preloadAudio, resumeAudio, playStart, playShoot, playBounce,
  playPop, playDrop, playGrow, playLose,
} from './audio';

const canvas = document.getElementById('game') as HTMLCanvasElement;
const ctx = setupCanvas(canvas);
const theme = readTheme();

// Fit the canvas into the viewport preserving the 440:640 aspect (letterboxed).
// The backing store stays W·dpr × H·dpr; only the CSS box size changes here.
function fitCanvas(): void {
  const scale = Math.min(window.innerWidth / W, window.innerHeight / H);
  canvas.style.width = Math.round(W * scale) + 'px';
  canvas.style.height = Math.round(H * scale) + 'px';
}
fitCanvas();
window.addEventListener('resize', fitCanvas);
window.addEventListener('orientationchange', fitCanvas);
loadBackground();
preloadAudio();

function getBest(): number {
  try { return Number(localStorage.getItem(BEST_KEY)) || 0; } catch { return 0; }
}
function saveBest(score: number): void {
  try { if (score > getBest()) localStorage.setItem(BEST_KEY, String(score)); } catch { /* ignore */ }
}

let state: GameState = createState(getBest());
let effects: Effect[] = [];
let gridSlideStart = -Infinity;
let menuOpen = false;

function gridShiftY(now: number): number {
  const t = (now - gridSlideStart) / 200;
  if (t < 0 || t > 1) return 0;
  return -ROW_H * (1 - t);    // new row slides in from above
}

function spawnParticles(x: number, y: number, color: Color, now: number): void {
  for (let i = 0; i < 6; i++) {
    const a = Math.random() * Math.PI * 2;
    const sp = 40 + Math.random() * 80;
    effects.push({
      kind: 'particle', x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
      r: 2 + Math.random() * 2, color, startMs: now, dur: 350,
    });
  }
}

function startGame(): void {
  state = { ...createState(getBest()), phase: 'playing' };
  effects = [];
  gridSlideStart = -Infinity;
  menuOpen = false;
  resumeAudio();
  playStart();
  track('game_start');
}

function fire(): void {
  if (state.phase !== 'playing' || state.projectile) return;
  const a = state.aimAngle;
  state = {
    ...state,
    projectile: {
      x: LAUNCHER_X, y: LAUNCHER_Y - R,
      vx: Math.cos(a) * SHOT_SPEED, vy: -Math.sin(a) * SHOT_SPEED,
      color: state.current,
    },
    current: state.next,
    next: pickColor(state.bubbles),
  };
  resumeAudio();
  playShoot();
}

function land(projX: number, projY: number, color: Color, now: number): void {
  const { row, col } = snapCell(projX, projY, state.dropCount, occupiedSet(state.bubbles));
  const res = applyShot(state.bubbles, row, col, color, state.dropCount);

  const snapC = cellCenter(row, col, state.dropCount);
  effects.push({ kind: 'snap', x: snapC.x, y: snapC.y, color, startMs: now, dur: 120 });

  if (res.popped.length) {
    playPop();
    for (const b of res.popped) {
      const c = bubbleCenter(b, state.dropCount);
      effects.push({ kind: 'pop', x: c.x, y: c.y, color: b.color, startMs: now, dur: 150 });
      spawnParticles(c.x, c.y, b.color, now);
    }
  }
  if (res.dropped.length) {
    playDrop();
    for (const b of res.dropped) {
      const c = bubbleCenter(b, state.dropCount);
      effects.push({ kind: 'fall', x: c.x, y: c.y, vy: 120, color: b.color, startMs: now, dur: 400 });
    }
  }

  let bubbles = res.bubbles;
  let dropCount = state.dropCount;
  const score = state.score + res.scoreGained;
  let dropCounter = state.dropCounter - 1;

  if (dropCounter <= 0) {
    dropCounter = DROP_INTERVAL;
    if (!isWin(bubbles)) {
      const d = dropCeiling({ ...state, bubbles, dropCount });
      bubbles = d.bubbles;
      dropCount = d.dropCount;
      gridSlideStart = now;
      playGrow();
    }
  }

  // Keep loaded/preview colours valid (only colours present on the field).
  let current = state.current, next = state.next;
  if (bubbles.size > 0) {
    const present = new Set(colorsOnField(bubbles));
    if (!present.has(current)) current = pickColor(bubbles);
    if (!present.has(next)) next = pickColor(bubbles);
  }

  let phase = state.phase;
  const prevBest = getBest();
  if (isWin(bubbles)) { phase = 'win'; saveBest(score); }
  else if (isLost(bubbles)) { phase = 'lost'; playLose(); saveBest(score); }
  if (phase !== 'playing') {
    if (score > prevBest) track('new_best', { score });
    track('game_over', { result: phase === 'win' ? 'win' : 'lose', score });
    menuOpen = false; // can't stick past the active phase
  }

  state = {
    ...state, bubbles, dropCount, dropCounter, score, current, next,
    projectile: null, phase, best: Math.max(state.best, score),
  };
}

attachInput(canvas, {
  getPhase: () => state.phase,
  isMenuOpen: () => menuOpen,
  aimAt: (x, y) => { state = { ...state, aimAngle: aimToward(x, y, LAUNCHER_Y) }; },
  fire,
  pressButton: (name) => {
    if (name === 'play' || name === 'restart' || name === 'again') startGame();
    else if (name === 'menu') menuOpen = true;
    else if (name === 'resume') menuOpen = false;
    else if (name === 'hub') location.href = document.body.dataset.hubUrl ?? '/';
  },
});

document.addEventListener('touchstart', resumeAudio, { once: true });
document.addEventListener('click', resumeAudio, { once: true });
document.addEventListener('visibilitychange', () => { if (!document.hidden) resumeAudio(); });

let lastTime = 0;
function frame(now: number): void {
  try {
    const dt = lastTime ? Math.min((now - lastTime) / 1000, 0.05) : 0;
    lastTime = now;

    if (state.phase === 'playing' && !menuOpen && state.projectile) {
      const fs = advanceProjectile(state.projectile, dt, state.bubbles, state.dropCount);
      if (fs.bounced) playBounce();
      if (fs.landed) land(fs.proj.x, fs.proj.y, fs.proj.color, now);
      else state = { ...state, projectile: fs.proj };
    }

    effects = effects.filter((e) => now - e.startMs < e.dur);
    renderFrame(ctx, state, theme, effects, now, gridShiftY(now), menuOpen);
  } catch (e) {
    console.error('[bubble] loop error', e);
  }
  requestAnimationFrame(frame);
}

requestAnimationFrame(frame);
